import { useCallback, useId, useMemo, useRef, useState } from "react"

import { useEvent } from "shared/lib/useEvent"

import {
  GLOBAL_SEARCH_ALL_TAB,
  GLOBAL_SEARCH_HISTORY_OPTION_TYPE,
  GlobalSearchActiveTab,
  GlobalSearchOption,
  GlobalSearchOptionsMap,
  GlobalSearchResultType,
  GlobalSearchSelectableOption
} from "features/global-search/types/GlobalSearch"

import { GlobalSearchContextValue } from "../GlobalSearchContext"
import { GlobalSearchInputContextValue } from "../GlobalSearchInputContext"
import { GlobalSearchNavigationContextValue } from "../GlobalSearchNavigationContext"
import { useGlobalSearchKeyboardNavigation } from "../lib/useGlobalSearchKeyboardNavigation"

export interface GlobalSearchViewModelProps {
  searchTerm: string | null
  onSearchTermChange: (value: string | null) => void
  options: GlobalSearchOptionsMap
  isLoading: boolean
  onChange: (option: GlobalSearchOption | null) => void
  history: string[]
}

export interface GlobalSearchViewModel {
  searchContextValue: GlobalSearchContextValue
  inputContextValue: GlobalSearchInputContextValue
  navigationContextValue: GlobalSearchNavigationContextValue
}

export function useGlobalSearchViewModel({
  searchTerm,
  onSearchTermChange,
  options,
  isLoading,
  onChange,
  history
}: GlobalSearchViewModelProps): GlobalSearchViewModel {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<GlobalSearchActiveTab>(
    GLOBAL_SEARCH_ALL_TAB
  )
  const [inputValue, setInputValue] = useState(searchTerm ?? "")
  const [statusId, setStatusId] = useState<string | undefined>(undefined)
  const onStatusRegister = useEvent((id: string) => setStatusId(id))
  const onActiveTabChange = useEvent((tab: GlobalSearchActiveTab) => {
    setActiveIndex(null)
    setActiveTab(tab)
  })

  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map())
  const optionRefs = useRef<Map<number, GlobalSearchSelectableOption>>(
    new Map()
  )
  const keyboardActiveRef = useRef(false)
  const listboxId = useId()

  const register = useEvent((index: number, ref: HTMLButtonElement) => {
    itemRefs.current.set(index, ref)
  })

  const unregister = useEvent((index: number) => {
    itemRefs.current.delete(index)
    optionRefs.current.delete(index)
    setActiveIndex(prev => (prev === index ? null : prev))
  })

  const updateOption = useEvent(
    (index: number, option: GlobalSearchSelectableOption) => {
      optionRefs.current.set(index, option)
    }
  )

  const activateItem = useEvent((index: number | null) => {
    setActiveIndex(index)
    if (index !== null) {
      itemRefs.current
        .get(index)
        ?.scrollIntoView({ behavior: "instant", block: "nearest" })
    }
  })

  const hasResults = useMemo(
    () =>
      Object.values(GlobalSearchResultType).some(
        type => (options[type]?.options.length ?? 0) > 0
      ),
    [options]
  )

  const handleChange = useEvent(
    (option: GlobalSearchSelectableOption | null) => {
      if (option?.type === GLOBAL_SEARCH_HISTORY_OPTION_TYPE) {
        setInputValue(option.value)
        onSearchTermChange(option.value)
      } else {
        onChange(option)
      }
    }
  )

  const [onInputKeyDown, onContainerKeyDown] =
    useGlobalSearchKeyboardNavigation(
      itemRefs,
      optionRefs,
      activeIndex,
      activateItem,
      keyboardActiveRef,
      handleChange
    )

  const inputContextValue = useMemo<GlobalSearchInputContextValue>(
    () => ({ inputValue, onInputValueChange: setInputValue }),
    [inputValue]
  )

  const searchContextValue = useMemo<GlobalSearchContextValue>(
    () => ({
      searchTerm,
      onSearchTermChange,
      options,
      hasResults,
      isLoading,
      onChange: handleChange,
      activeTab,
      onActiveTabChange,
      statusId,
      onStatusRegister,
      history
    }),
    [
      searchTerm,
      onSearchTermChange,
      options,
      hasResults,
      isLoading,
      handleChange,
      activeTab,
      onActiveTabChange,
      statusId,
      onStatusRegister,
      history
    ]
  )

  const getItemId = useCallback(
    (index: number) => `${listboxId}-${index}`,
    [listboxId]
  )

  const navigationContextValue = useMemo<GlobalSearchNavigationContextValue>(
    () => ({
      activeIndex,
      listboxId,
      getItemId,
      activateItem,
      register,
      unregister,
      updateOption,
      onInputKeyDown,
      onContainerKeyDown,
      keyboardActiveRef
    }),
    [
      activeIndex,
      listboxId,
      getItemId,
      activateItem,
      register,
      unregister,
      updateOption,
      onInputKeyDown,
      onContainerKeyDown
    ]
  )

  return { searchContextValue, inputContextValue, navigationContextValue }
}
